/** @jsxImportSource @emotion/react */
import { useMemo, useState } from 'react'
import styled from '@emotion/styled'
import ReactSelect from 'react-select'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import { physicianFormalName } from 'helpers/nameHelper'
import { TableWrapper } from 'components/atoms/TableWrapper'
import Button from 'components/atoms/Button'
import DataTable from 'components/atoms/DataTable'
import { confirm } from 'components/molecules/Confirmation'
import { useGetFollowUpPhysiciansQuery } from 'store/services/labPhysician'
import {
  useAddOfficeFollowUpPhysicianMutation,
  useGetOfficeFollowUpPhysiciansQuery,
  useGetOfficeSettingsQuery,
  useRemoveOfficeFollowUpPhysicianMutation,
  useUpdateOfficeSettingsMutation,
} from 'store/services/office'
import { isOrganization } from 'helpers/followUpProvidersHelper'
import routes from '../constants/routes'

interface IProps {
  officeId: number
}

type SelectOption = {
  value: string
  label: string
}

const StyledTableWrapper = styled(TableWrapper)`
  th {
    padding: 0 0.2rem 0.4rem 0.1rem;
  }
  td {
    vertical-align: middle;
    padding: 0.4rem;
    div {
      span {
        font-size: 0.9rem;
      }
    }
  }
`

export const OfficeFollowUpProvidersSection = ({ officeId }: IProps) => {
  const navigate = useNavigate()
  const [selectedProvider, setSelectedProvider] = useState<SelectOption | null>(null)

  const {
    data: followUpPhysicians,
    isLoading,
    refetch,
  } = useGetOfficeFollowUpPhysiciansQuery({ id: officeId })

  const { data: availableProviders, isLoading: isLoadingProviders } = useGetFollowUpPhysiciansQuery(
    {},
  )
  const { data: officeSettings } = useGetOfficeSettingsQuery({ id: officeId })

  const [addFollowUpPhysician, { isLoading: isAdding }] = useAddOfficeFollowUpPhysicianMutation()
  const [removeFollowUpPhysician] = useRemoveOfficeFollowUpPhysicianMutation()
  const [updateOfficeSettings] = useUpdateOfficeSettingsMutation()

  const defaultFollowUpPhysicianId = officeSettings?.defaultFollowUpPhysicianId ?? undefined
  const isFollowUpCareNone = officeSettings?.isFollowUpCareNone ?? false

  const availableOptions = useMemo(() => {
    if (!availableProviders) return []
    const assignedNpis = followUpPhysicians?.map((p) => p.followUpPhysician?.npi) || []

    return availableProviders
      .filter((p) => !assignedNpis.includes(p.npi))
      .map((p) => ({
        value: p.npi,
        label: isOrganization(p.providerType)
          ? `${p.organizationName} - NPI: ${p.npi}`
          : `${physicianFormalName(p.firstName, p.lastName, p.credential)} - NPI: ${p.npi}`,
      }))
  }, [availableProviders, followUpPhysicians])

  const persistDefault = async (id: number | null) => {
    await updateOfficeSettings({
      id: officeId,
      defaultFollowUpPhysicianId: id,
    }).unwrap()
  }

  const handleAddProvider = async () => {
    if (!selectedProvider) return

    try {
      await addFollowUpPhysician({
        id: officeId,
        followUpPhysicianNpi: selectedProvider.value,
      }).unwrap()
      setSelectedProvider(null)
      refetch()
    } catch (error) {
      console.error('Error adding office follow-up physician:', error)
      toast.error('Failed to add follow-up care provider.')
    }
  }

  const handleRemoveProvider = async (npi: string, followUpPhysicianId: number) => {
    confirm('', {
      yesAction: 'Yes',
      description: 'Are you sure you want to remove this follow-up care provider from the office?',
    })
      .then(async () => {
        try {
          await removeFollowUpPhysician({ id: officeId, npi }).unwrap()
          if (
            defaultFollowUpPhysicianId !== undefined &&
            Number(followUpPhysicianId) === Number(defaultFollowUpPhysicianId)
          ) {
            await persistDefault(null)
          }
          refetch()
        } catch (error) {
          console.error('Error removing office follow-up physician:', error)
          toast.error('Failed to remove follow-up care provider.')
        }
      })
      .catch(() => {})
  }

  const handleSetDefault = async (followUpPhysicianId: number) => {
    try {
      await persistDefault(followUpPhysicianId)
    } catch (error) {
      console.error('Error setting office default follow-up physician:', error)
      toast.error('Failed to set default follow-up care provider.')
    }
  }

  const handleClearDefault = async () => {
    try {
      await persistDefault(null)
    } catch (error) {
      console.error('Error clearing office default follow-up physician:', error)
      toast.error('Failed to clear default follow-up care provider.')
    }
  }

  const handleToggleFollowUpCareNone = async (checked: boolean) => {
    try {
      await updateOfficeSettings({
        id: officeId,
        isFollowUpCareNone: checked,
      }).unwrap()
    } catch (error) {
      console.error('Error updating office follow-up care none flag:', error)
      toast.error('Failed to update follow-up care setting.')
    }
  }

  const tableColumns = [
    {
      accessor: 'providerType',
      Header: 'Provider Type',
      disableSortBy: true,
      Cell: ({ row }) => {
        const followUpPhysician = row.original.followUpPhysician
        return (
          <div>
            {isOrganization(followUpPhysician?.providerType) ? 'Organization' : 'Individual'}
          </div>
        )
      },
    },
    {
      accessor: 'name',
      Header: 'Name',
      disableSortBy: true,
      Cell: ({ row }) => {
        const followUpPhysician = row.original?.followUpPhysician
        const label = isOrganization(followUpPhysician?.providerType)
          ? followUpPhysician?.organizationName
          : physicianFormalName(
              followUpPhysician?.firstName,
              followUpPhysician?.lastName,
              followUpPhysician?.credential,
            )
        return (
          <Button
            css={{ all: 'unset' }}
            label={label}
            onClick={() =>
              navigate(
                `${routes.index}${routes.physician.root}${routes.physician.upsert}${row.original?.followUpPhysician?.npi}`,
              )
            }
          />
        )
      },
    },
    {
      accessor: 'npi',
      Header: 'NPI',
      disableSortBy: true,
      Cell: ({ row }) => <div>{row.original.followUpPhysician?.npi}</div>,
    },
    {
      accessor: 'isDefault',
      Header: 'Default',
      disableSortBy: true,
      Cell: ({ row }) => {
        const isDefault =
          defaultFollowUpPhysicianId !== undefined &&
          Number(row.original.followUpPhysicianId) === Number(defaultFollowUpPhysicianId)

        return (
          <div>
            {isDefault ? (
              <button
                type="button"
                className="btn btn-link btn-sm p-0"
                onClick={handleClearDefault}
                title="Click to unset as default">
                <i className="fa fa-star text-warning" css={{ fontSize: '1.2rem' }} />
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-link btn-sm p-0"
                onClick={() => handleSetDefault(Number(row.original.followUpPhysicianId))}
                title="Set as default">
                <i className="far fa-star text-muted" css={{ fontSize: '1.2rem' }} />
              </button>
            )}
          </div>
        )
      },
    },
    {
      id: 'actions',
      Header: '',
      disableSortBy: true,
      Cell: ({ row }) => (
        <button
          type="button"
          className="btn btn-outline-danger btn-sm"
          onClick={() =>
            handleRemoveProvider(
              row.original.followUpPhysician?.npi,
              row.original.followUpPhysicianId,
            )
          }>
          Remove
        </button>
      ),
    },
  ]

  return (
    <div className="form-group textGray mt-3">
      <label className="form-label">Follow-up Care Providers</label>
      <div className="mb-2">
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="officeFollowUpCareNone"
            checked={isFollowUpCareNone}
            onChange={(e) => handleToggleFollowUpCareNone(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="officeFollowUpCareNone">
            No follow-up care
          </label>
        </div>
      </div>
      {!isFollowUpCareNone && (
        <>
          <div className="row align-items-end mb-3">
            <div className="col-sm-8">
              <ReactSelect
                className="basic-single"
                classNamePrefix="select"
                isClearable
                isSearchable
                isLoading={isLoadingProviders}
                placeholder="Search for a follow-up physician..."
                value={selectedProvider}
                onChange={(option) => setSelectedProvider(option as SelectOption | null)}
                options={availableOptions}
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 10000 }) }}
              />
            </div>
            <div className="col-sm-4">
              <Button
                className="btn btn-primary"
                label="Add Physician"
                icon="fa fa-plus"
                disabled={!selectedProvider || isAdding}
                onClick={handleAddProvider}
              />
            </div>
          </div>
          <StyledTableWrapper className="card-body row">
            <DataTable
              tableKey={'office_follow_up_physicians'}
              data={{ items: followUpPhysicians || [] }}
              status={isLoading}
              tableColumns={tableColumns}
              pageSize={10}
              entityName="follow-up physician"
            />
          </StyledTableWrapper>
        </>
      )}
    </div>
  )
}
